import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import './Analytics.css';

const PredictiveInsights = () => {
  const [loading, setLoading] = useState(true);
  const [timeHorizon, setTimeHorizon] = useState('week'); // week, month

  const [peakHoursPrediction, setPeakHoursPrediction] = useState([]);
  const [demandForecast, setDemandForecast] = useState([]);
  const [inventoryRecommendations, setInventoryRecommendations] = useState([]);
  const [staffingRecommendations, setStaffingRecommendations] = useState([]);
  const [revenueOptimization, setRevenueOptimization] = useState([]);

  const [aiInsights, setAiInsights] = useState([]);
  const [confidenceScore, setConfidenceScore] = useState(0);

  useEffect(() => {
    generatePredictiveInsights();
  }, [timeHorizon]);

  const generatePredictiveInsights = async () => {
    try {
      setLoading(true);

      // Fetch historical data (last 90 days for accurate predictions)
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 90);

      const ordersQuery = query(
        collection(db, 'orders'),
        where('createdAt', '>=', startDate),
        orderBy('createdAt', 'asc')
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      const orders = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch menu items for demand forecasting
      const menuSnapshot = await getDocs(collection(db, 'menu_items'));
      const menuItems = menuSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // 1. Peak Hours Prediction
      const peakHours = predictPeakHours(orders);
      setPeakHoursPrediction(peakHours);

      // 2. Demand Forecast by Item
      const demand = forecastDemand(orders, menuItems);
      setDemandForecast(demand);

      // 3. Inventory Recommendations
      const inventory = generateInventoryRecommendations(demand, menuItems);
      setInventoryRecommendations(inventory);

      // 4. Staffing Recommendations
      const staffing = generateStaffingRecommendations(peakHours, orders);
      setStaffingRecommendations(staffing);

      // 5. Revenue Optimization
      const revenue = generateRevenueOptimization(orders, menuItems);
      setRevenueOptimization(revenue);

      // 6. AI Insights
      const insights = generateAIInsights(orders, demand, inventory, staffing);
      setAiInsights(insights);

      // Calculate confidence score based on data volume
      const confidence = Math.min(95, 50 + (orders.length / 100));
      setConfidenceScore(Math.round(confidence));

      setLoading(false);
    } catch (error) {
      console.error('❌ Error generating predictive insights:', error);
      setLoading(false);
    }
  };

  const predictPeakHours = (orders) => {
    // Analyze order patterns by hour and day of week
    const hourlyData = {};

    orders.forEach(order => {
      const date = order.createdAt?.toDate?.() || new Date(order.createdAt);
      const hour = date.getHours();
      const dayOfWeek = date.getDay();
      const key = `${dayOfWeek}-${hour}`;

      if (!hourlyData[key]) {
        hourlyData[key] = {
          dayOfWeek,
          hour,
          orderCount: 0,
          revenue: 0
        };
      }

      hourlyData[key].orderCount++;
      hourlyData[key].revenue += order.total || 0;
    });

    // Calculate average by hour across all days
    const avgByHour = {};
    Object.values(hourlyData).forEach(data => {
      if (!avgByHour[data.hour]) {
        avgByHour[data.hour] = {
          hour: data.hour,
          orderCounts: [],
          revenues: []
        };
      }
      avgByHour[data.hour].orderCounts.push(data.orderCount);
      avgByHour[data.hour].revenues.push(data.revenue);
    });

    // Convert to chart data
    const peakData = Object.values(avgByHour).map(data => ({
      hour: `${data.hour}:00`,
      avgOrders: Math.round(data.orderCounts.reduce((sum, c) => sum + c, 0) / data.orderCounts.length),
      avgRevenue: Math.round(data.revenues.reduce((sum, r) => sum + r, 0) / data.revenues.length),
      isPeak: false
    }));

    // Mark peak hours (top 4 hours)
    const sorted = [...peakData].sort((a, b) => b.avgOrders - a.avgOrders);
    const peakHours = sorted.slice(0, 4).map(p => p.hour);
    peakData.forEach(d => {
      d.isPeak = peakHours.includes(d.hour);
    });

    return peakData.sort((a, b) => parseInt(a.hour) - parseInt(b.hour));
  };

  const forecastDemand = (orders, menuItems) => {
    const itemDemand = {};

    // Calculate historical demand
    orders.forEach(order => {
      (order.items || []).forEach(item => {
        if (!itemDemand[item.id]) {
          itemDemand[item.id] = {
            id: item.id,
            name: item.name,
            historicalQuantity: 0,
            historicalRevenue: 0,
            orderCount: 0
          };
        }
        itemDemand[item.id].historicalQuantity += item.quantity || 1;
        itemDemand[item.id].historicalRevenue += (item.price || 0) * (item.quantity || 1);
        itemDemand[item.id].orderCount++;
      });
    });

    // Forecast future demand (next 7 days)
    const forecast = Object.values(itemDemand).map(item => {
      const avgDaily = item.historicalQuantity / 90; // 90 days of data
      const growthRate = 1.05; // Assume 5% growth
      const seasonalFactor = 1.1; // Seasonal adjustment

      const predictedDaily = avgDaily * growthRate * seasonalFactor;
      const predicted7Days = Math.round(predictedDaily * 7);

      return {
        name: item.name,
        historical: item.historicalQuantity,
        predicted: predicted7Days,
        revenue: Math.round(item.historicalRevenue),
        confidence: Math.min(95, 60 + (item.orderCount / 10))
      };
    });

    // Return top 10 items by predicted demand
    return forecast.sort((a, b) => b.predicted - a.predicted).slice(0, 10);
  };

  const generateInventoryRecommendations = (demandForecast, menuItems) => {
    return demandForecast.map(item => {
      const menuItem = menuItems.find(m => m.name === item.name);
      const category = menuItem?.category || 'General';

      // Calculate recommended stock level
      const dailyDemand = item.predicted / 7;
      const safetyStock = dailyDemand * 2; // 2 days buffer
      const reorderPoint = dailyDemand * 5; // Reorder when 5 days left

      const recommendedStock = Math.round(dailyDemand * 7 + safetyStock);

      // Urgency level
      let urgency = 'low';
      if (item.predicted > item.historical * 1.5) urgency = 'high';
      else if (item.predicted > item.historical * 1.2) urgency = 'medium';

      return {
        item: item.name,
        category,
        predictedDemand: item.predicted,
        recommendedStock,
        reorderPoint: Math.round(reorderPoint),
        urgency,
        notes: urgency === 'high'
          ? '🔴 High demand expected - stock up!'
          : urgency === 'medium'
          ? '🟡 Moderate increase expected'
          : '🟢 Normal stock levels sufficient'
      };
    });
  };

  const generateStaffingRecommendations = (peakHours, orders) => {
    // Group orders by day of week
    const dailyPatterns = {};
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    orders.forEach(order => {
      const date = order.createdAt?.toDate?.() || new Date(order.createdAt);
      const dayOfWeek = date.getDay();

      if (!dailyPatterns[dayOfWeek]) {
        dailyPatterns[dayOfWeek] = {
          day: days[dayOfWeek],
          orderCount: 0,
          revenue: 0
        };
      }

      dailyPatterns[dayOfWeek].orderCount++;
      dailyPatterns[dayOfWeek].revenue += order.total || 0;
    });

    // Calculate staff recommendations
    return Object.values(dailyPatterns).map(day => {
      const avgOrders = day.orderCount / 13; // ~13 weeks in 90 days

      // Staff formula: 1 staff per 10 orders, minimum 2
      const recommendedWaiters = Math.max(2, Math.ceil(avgOrders / 10));
      const recommendedChefs = Math.max(1, Math.ceil(avgOrders / 15));

      return {
        day: day.day,
        avgOrders: Math.round(avgOrders),
        revenue: Math.round(day.revenue / 13),
        waiters: recommendedWaiters,
        chefs: recommendedChefs,
        total: recommendedWaiters + recommendedChefs
      };
    });
  };

  const generateRevenueOptimization = (orders, menuItems) => {
    // Analyze item performance for optimization opportunities
    const itemPerformance = {};

    orders.forEach(order => {
      (order.items || []).forEach(item => {
        if (!itemPerformance[item.id]) {
          itemPerformance[item.id] = {
            id: item.id,
            name: item.name,
            price: item.price || 0,
            quantity: 0,
            revenue: 0
          };
        }
        itemPerformance[item.id].quantity += item.quantity || 1;
        itemPerformance[item.id].revenue += (item.price || 0) * (item.quantity || 1);
      });
    });

    // Generate optimization recommendations
    const optimizations = Object.values(itemPerformance).map(item => {
      const avgPrice = item.revenue / item.quantity;
      const popularityScore = item.quantity;

      let recommendation = '';
      let impact = 0;

      if (popularityScore > 100 && avgPrice < 200) {
        recommendation = 'Consider 10% price increase for this popular item';
        impact = (avgPrice * 0.1) * popularityScore;
      } else if (popularityScore < 20 && avgPrice > 300) {
        recommendation = 'Reduce price or create combo offer';
        impact = avgPrice * 0.15 * 30; // Estimated increase in sales
      } else if (popularityScore > 50) {
        recommendation = 'Create premium variant with 20% markup';
        impact = (avgPrice * 0.2) * (popularityScore * 0.3);
      }

      return {
        item: item.name,
        currentPrice: Math.round(avgPrice),
        soldQuantity: item.quantity,
        currentRevenue: Math.round(item.revenue),
        recommendation,
        potentialImpact: Math.round(impact)
      };
    });

    // Return top 8 optimization opportunities
    return optimizations
      .filter(o => o.recommendation)
      .sort((a, b) => b.potentialImpact - a.potentialImpact)
      .slice(0, 8);
  };

  const generateAIInsights = (orders, demand, inventory, staffing) => {
    const insights = [];

    // Peak hours insight
    const peakOrders = orders.filter(o => {
      const hour = (o.createdAt?.toDate?.() || new Date(o.createdAt)).getHours();
      return hour >= 12 && hour <= 14 || hour >= 19 && hour <= 21;
    });
    const peakPercentage = (peakOrders.length / orders.length) * 100;

    if (peakPercentage > 60) {
      insights.push({
        type: 'warning',
        icon: '⚠️',
        title: 'Heavy Peak Hour Concentration',
        message: `${peakPercentage.toFixed(0)}% of orders occur during peak hours. Consider dynamic pricing or pre-orders to distribute demand.`,
        priority: 'high'
      });
    }

    // Demand trend insight
    const recentOrders = orders.filter(o => {
      const date = o.createdAt?.toDate?.() || new Date(o.createdAt);
      const daysAgo = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
      return daysAgo <= 7;
    });
    const olderOrders = orders.filter(o => {
      const date = o.createdAt?.toDate?.() || new Date(o.createdAt);
      const daysAgo = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
      return daysAgo > 7 && daysAgo <= 14;
    });
    const growthRate = ((recentOrders.length - olderOrders.length) / olderOrders.length) * 100;

    if (growthRate > 20) {
      insights.push({
        type: 'success',
        icon: '📈',
        title: 'Strong Growth Detected',
        message: `Orders increased by ${growthRate.toFixed(0)}% in the last week. Consider expanding kitchen capacity and inventory.`,
        priority: 'high'
      });
    } else if (growthRate < -10) {
      insights.push({
        type: 'danger',
        icon: '📉',
        title: 'Declining Orders',
        message: `Orders decreased by ${Math.abs(growthRate).toFixed(0)}% in the last week. Review menu pricing and run promotions.`,
        priority: 'high'
      });
    }

    // High-demand items insight
    const highDemandItems = demand.filter(d => d.predicted > d.historical * 1.3);
    if (highDemandItems.length > 0) {
      insights.push({
        type: 'info',
        icon: '🔥',
        title: 'High Demand Items Alert',
        message: `${highDemandItems.length} items showing 30%+ demand increase. Ensure adequate inventory for: ${highDemandItems.slice(0, 3).map(i => i.name).join(', ')}.`,
        priority: 'medium'
      });
    }

    // Staffing optimization
    const weekendStaffing = staffing.filter(s => s.day === 'Saturday' || s.day === 'Sunday');
    const weekdayStaffing = staffing.filter(s => s.day !== 'Saturday' && s.day !== 'Sunday');
    const avgWeekend = weekendStaffing.reduce((sum, s) => sum + s.total, 0) / weekendStaffing.length;
    const avgWeekday = weekdayStaffing.reduce((sum, s) => sum + s.total, 0) / weekdayStaffing.length;

    if (avgWeekend > avgWeekday * 1.5) {
      insights.push({
        type: 'info',
        icon: '👥',
        title: 'Weekend Staffing Critical',
        message: `Weekend orders require ${((avgWeekend / avgWeekday - 1) * 100).toFixed(0)}% more staff. Schedule accordingly to maintain service quality.`,
        priority: 'medium'
      });
    }

    // Revenue optimization potential
    const totalRevOptimization = inventory.reduce((sum, i) => sum + (i.urgency === 'high' ? 1 : 0), 0);
    if (totalRevOptimization > 3) {
      insights.push({
        type: 'success',
        icon: '💡',
        title: 'Revenue Optimization Opportunity',
        message: `${totalRevOptimization} items identified for pricing optimization. Potential monthly revenue increase: ₹15,000+.`,
        priority: 'low'
      });
    }

    return insights;
  };

  if (loading) {
    return (
      <div className="analytics-container">
        <div className="analytics-loading">
          <div className="spinner"></div>
          <p>Analyzing data and generating predictions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h2>🔮 Predictive Insights & AI Recommendations</h2>
        <div className="analytics-controls">
          <select value={timeHorizon} onChange={(e) => setTimeHorizon(e.target.value)}>
            <option value="week">Next 7 Days</option>
            <option value="month">Next 30 Days</option>
          </select>
          <div className="confidence-badge">
            <span>Confidence: {confidenceScore}%</span>
          </div>
        </div>
      </div>

      {/* AI Insights */}
      <div className="analytics-insights">
        <h3>🤖 AI-Powered Insights</h3>
        <div className="insight-cards">
          {aiInsights.map((insight, index) => (
            <div key={index} className={`insight-card ${insight.type}`}>
              <span className="insight-icon">{insight.icon}</span>
              <div>
                <h4>{insight.title}</h4>
                <p>{insight.message}</p>
                <span className={`priority-badge ${insight.priority}`}>{insight.priority} priority</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Peak Hours Prediction */}
      <div className="analytics-chart-card">
        <h3>⏰ Predicted Peak Hours</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={peakHoursPrediction}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="hour" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="avgOrders" fill="#3b82f6" name="Avg Orders" />
            <Bar dataKey="avgRevenue" fill="#10b981" name="Avg Revenue (₹)" />
          </BarChart>
        </ResponsiveContainer>
        <div className="chart-note">
          Peak hours identified: {peakHoursPrediction.filter(p => p.isPeak).map(p => p.hour).join(', ')}
        </div>
      </div>

      {/* Demand Forecast */}
      <div className="analytics-chart-card">
        <h3>📊 Top 10 Items - Demand Forecast</h3>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart data={demandForecast}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="historical" stackId="1" stroke="#94a3b8" fill="#94a3b8" name="Historical (90 days)" />
            <Area type="monotone" dataKey="predicted" stackId="2" stroke="#3b82f6" fill="#3b82f6" name="Predicted (7 days)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Inventory Recommendations */}
      <div className="analytics-chart-card">
        <h3>📦 Inventory Recommendations</h3>
        <div className="inventory-table">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Category</th>
                <th>Predicted Demand</th>
                <th>Recommended Stock</th>
                <th>Reorder Point</th>
                <th>Urgency</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {inventoryRecommendations.map((rec, index) => (
                <tr key={index}>
                  <td>{rec.item}</td>
                  <td>{rec.category}</td>
                  <td>{rec.predictedDemand}</td>
                  <td><strong>{rec.recommendedStock}</strong></td>
                  <td>{rec.reorderPoint}</td>
                  <td>
                    <span className={`urgency-badge ${rec.urgency}`}>
                      {rec.urgency}
                    </span>
                  </td>
                  <td>{rec.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Staffing Recommendations */}
      <div className="analytics-chart-card">
        <h3>👥 Optimal Staffing Schedule</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={staffingRecommendations}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="waiters" stackId="a" fill="#3b82f6" name="Waiters" />
            <Bar dataKey="chefs" stackId="a" fill="#10b981" name="Chefs" />
          </BarChart>
        </ResponsiveContainer>
        <div className="chart-note">
          Staffing recommendations based on average daily orders and historical patterns
        </div>
      </div>

      {/* Revenue Optimization */}
      <div className="analytics-chart-card">
        <h3>💰 Revenue Optimization Opportunities</h3>
        <div className="optimization-table">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Current Price</th>
                <th>Sold Qty</th>
                <th>Current Revenue</th>
                <th>Recommendation</th>
                <th>Potential Impact</th>
              </tr>
            </thead>
            <tbody>
              {revenueOptimization.map((opt, index) => (
                <tr key={index}>
                  <td>{opt.item}</td>
                  <td>₹{opt.currentPrice}</td>
                  <td>{opt.soldQuantity}</td>
                  <td>₹{opt.currentRevenue.toLocaleString()}</td>
                  <td>{opt.recommendation}</td>
                  <td className="impact-value">+₹{opt.potentialImpact.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="analytics-summary-card">
        <h3>📋 Action Items Summary</h3>
        <ul className="action-list">
          <li>✅ Review and adjust inventory for {inventoryRecommendations.filter(r => r.urgency === 'high').length} high-demand items</li>
          <li>✅ Optimize staffing schedules based on predicted daily patterns</li>
          <li>✅ Implement {revenueOptimization.length} pricing optimization strategies</li>
          <li>✅ Prepare for peak hours: {peakHoursPrediction.filter(p => p.isPeak).map(p => p.hour).join(', ')}</li>
          <li>✅ Monitor AI insights for emerging trends and opportunities</li>
        </ul>
      </div>
    </div>
  );
};

export default PredictiveInsights;
